import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Smartphone, Apple, Play, Bell, Sparkles, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { buildLocalizedPath } from '../components/LocaleRouter';

/**
 * /download-app — VietClinics mobile app pre-launch landing.
 *
 * The native apps aren't shipping yet, so this page captures intent
 * (waitlist via the existing contact form anchor) and surfaces the
 * same mobile mockup used on the homepage. Once the iOS/Android apps
 * are live, just swap the App Store / Play Store hrefs.
 */
const APP_MOCKUP = 'https://customer-assets.emergentagent.com/job_5a81bd9f-a300-47ec-b521-267995465c4c/artifacts/h0cpy1fw_WhatsApp%20Image%202026-05-22%20at%2014.15.35.png';

const DownloadAppPage = () => {
  const { language } = useLanguage();
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Download the VietClinics App — Coming Soon</title>
        <meta name="description" content="The VietClinics mobile app is launching soon — concierge healthcare in Vietnam from your pocket. Join the waitlist to be notified at launch." />
        <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/download-app` : ''} />
      </Helmet>

      <Header />

      <section className="relative overflow-hidden bg-white">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, #F0FAF8 0%, #FAFCFB 60%, #FFFFFF 100%)' }}
        />
        <div
          aria-hidden
          className="absolute top-[-10%] right-[-10%] rounded-full pointer-events-none"
          style={{
            width: 620, height: 620,
            background: 'radial-gradient(circle, rgba(31,167,161,0.20) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 flex flex-col gap-7">
              <div
                className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ background: '#FEF3C7', color: '#92400E' }}
                data-testid="download-app-eyebrow"
              >
                <Bell className="h-3.5 w-3.5" />
                Launching Soon · iOS &amp; Android
              </div>

              <h1
                className="font-extrabold tracking-tight text-slate-900 leading-[1.04]"
                style={{ fontSize: 'clamp(2.4rem, 4.8vw, 4rem)', letterSpacing: '-0.02em' }}
                data-testid="download-app-title"
              >
                The VietClinics app —<br />
                <span className="text-[#1FA7A1]">concierge in your pocket</span>
              </h1>

              <p className="text-slate-500 text-lg lg:text-xl leading-relaxed max-w-xl">
                Book treatments, chat with your care team, track your itinerary
                and pick up where you left off — all from one premium app.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl pt-2">
                {[
                  'Request treatments in one tap',
                  'Real-time chat with our concierge',
                  'Itinerary + clinic confirmations',
                  'Secure document vault',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #DCEFEC 0%, #F0FAF8 100%)' }}
                    >
                      <Sparkles className="h-4 w-4 text-[#1A8F8A]" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700 leading-snug pt-1.5">{point}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-3 bg-slate-900 text-white rounded-2xl px-6 py-3.5 cursor-not-allowed opacity-80"
                  title="iOS app coming soon"
                  data-testid="download-app-ios-btn"
                >
                  <Apple className="h-6 w-6" />
                  <div className="text-left leading-tight">
                    <p className="text-[10px] uppercase tracking-widest text-white/70">Coming soon</p>
                    <p className="text-sm font-bold">App Store</p>
                  </div>
                </button>
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-3 bg-slate-900 text-white rounded-2xl px-6 py-3.5 cursor-not-allowed opacity-80"
                  title="Android app coming soon"
                  data-testid="download-app-android-btn"
                >
                  <Play className="h-6 w-6" />
                  <div className="text-left leading-tight">
                    <p className="text-[10px] uppercase tracking-widest text-white/70">Coming soon</p>
                    <p className="text-sm font-bold">Google Play</p>
                  </div>
                </button>
                <Link
                  to={buildLocalizedPath('/#request-appointment', language)}
                  className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-2xl px-6 py-3.5 border-2 border-[#1FA7A1] hover:bg-[#1FA7A1]/5 transition-all"
                  data-testid="download-app-waitlist-btn"
                >
                  <Smartphone className="h-4 w-4" />
                  Join the waitlist
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <img
                src={APP_MOCKUP}
                alt="VietClinics mobile app preview"
                className="w-[220px] sm:w-[260px] lg:w-[300px]"
                style={{ filter: 'drop-shadow(0 30px 60px rgba(15,42,85,0.28))' }}
                data-testid="download-app-mockup"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DownloadAppPage;
